import { SortOrder } from "@/types/gql/graphql";
import { ArrowIcon, DeleteIcon, EditIcon } from "components/icons";
import { HeaderItem } from "@/components/data-table/types/table.types";
import { Policy, initPage } from "types";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { defaultCells, defaultSort } from "../policies.const";
import { formatDaysOfWeek, formatMaxStay, formatTimeOfDay } from "helper/policy";
import { formatPolicyColorToString, fromGraphQLColor, isEmpty } from "@/helper/utils";
import { colors } from "@/constants/map.const";
import { sortAsc, sortDesc } from "helper/array";
import { useContext, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { CityContext } from "contexts/city-context-provider";
import { IconButton } from "components";
import { Pagination } from "components/data-table/Pagination";
import { TableSkeleton } from "components/data-table";
import { cn } from "@/lib/utils";
import { formatDate } from "helper/date-time";
import { usePolicies } from "../hooks/use-policies";

export type PolicyTableProps = {
  selectPolicy: (input: Policy) => void;
  onDelete: (input: Policy) => void;
};

export type SortOption = {
  orderBy: keyof Policy;
  direction: SortOrder;
};

export const PolicyTable = ({ selectPolicy, onDelete }: PolicyTableProps) => {
  const { currentCity } = useContext(CityContext);
  const { policyList, loading } = usePolicies();
  const [currentPage, setCurrentPage] = useState({ ...initPage });
  const [headerCells, setHeaderCells] = useState<HeaderItem<keyof Policy>[]>(defaultCells);
  const [currentSort, setCurrentSort] = useState<SortOption>(defaultSort);

  const policyListSorted = useMemo(() => {
    if (loading) {
      return [];
    }

    if (currentSort.direction === SortOrder.Asc) {
      return sortAsc(Object.assign([], policyList), currentSort.orderBy);
    } else {
      return sortDesc(Object.assign([], policyList), currentSort.orderBy);
    }
  }, [loading, currentSort, policyList]);

  const policyListPaginate = useMemo(() => {
    return policyListSorted.slice(
      (currentPage.pageIndex - 1) * currentPage.pageSize,
      currentPage.pageSize * currentPage.pageIndex,
    );
  }, [policyListSorted, currentPage]);

  const onChangePage = (page: number) => {
    setCurrentPage({ ...currentPage, pageIndex: page });
  };

  if (!currentCity) {
    return <EmptyData />;
  }

  const handleSort = (orderBy?: keyof Policy, direction?: SortOrder) => {
    if (!orderBy || !direction) {
      return;
    }
    const newCells = headerCells.map((cell) => {
      if (cell.orderBy === orderBy) {
        const newDirection = direction === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc;
        return {
          ...cell,
          sortDirection: newDirection,
        };
      }
      return cell;
    });
    setCurrentSort({ orderBy, direction });
    setHeaderCells(newCells);
  };

  const totalPage = Math.ceil(policyList.length / currentPage.pageSize);

  return (
    <div className="flex flex-col w-full gap-y-2 ">
      <div className="w-full bottom-2 pb-8 h-full">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow>
              {headerCells.map((item, idx) => (
                <TableHead className={cn("px-6", item.className)} key={`policy-table-header-${idx}`}>
                  {item.title}
                  {item.sortable && (
                    <Button variant="ghost" onClick={() => handleSort(item.orderBy, item.sortDirection)}>
                      <ArrowIcon
                        className={cn("size-2", item.sortDirection === SortOrder.Asc ? "-rotate-90" : "rotate-90")}
                      />
                    </Button>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className="min-h-fit  max-h-full">
            {loading ? (
              <TableSkeleton />
            ) : (
              <>
                {isEmpty(policyListPaginate) ? (
                  <GenNoRecord colSpan={headerCells.length} message={loading ? "Loading..." : "No results found."} />
                ) : (
                  policyListPaginate.map((policy, idx) => (
                    <TableRow className="bg-white align-middle p-4 hover:none" key={`policy-item-${policy.id}`}>
                      <TableCell
                        key={`name-cell-${idx}`}
                        className="px-6 py-2 break-words whitespace-normal text-primary text-sm font-medium"
                      >
                        <div
                          style={{
                            borderStyle: policy.color?.dashed ? "dashed" : "solid",
                            borderWidth: policy.color?.lineColor ? "1px" : "0px",
                            borderColor: policy.color?.lineColor
                              ? formatPolicyColorToString(
                                  fromGraphQLColor(policy.color.lineColor, colors.PAYANT.lineColor),
                                )
                              : "",
                            backgroundColor: policy.color?.fillColor
                              ? formatPolicyColorToString(
                                  fromGraphQLColor(policy.color.fillColor, colors.PAYANT.fillColor),
                                )
                              : "",
                          }}
                          className="p-1"
                        >
                          {policy.name}
                        </div>
                      </TableCell>
                      <TableCell
                        key={`published-date-cell-${idx}`}
                        className="px-6 py-2 whitespace-nowrap text-primary text-sm font-medium"
                      >
                        {formatDate(policy.publishedDate || "", { format: "d MMM yyyy HH:mm" })}
                      </TableCell>
                      <TableCell
                        key={`category-cell-${idx}`}
                        className="px-6 py-2 whitespace-nowrap text-primary text-sm font-medium"
                      >
                        {policy.category || "-"}
                      </TableCell>
                      <TableCell
                        key={`max-stay-cell-${idx}`}
                        className="px-6 py-2 break-words whitespace-normal text-primary text-sm font-medium"
                      >
                        {formatMaxStay(policy.rules)}
                      </TableCell>
                      <TableCell
                        key={`days-of-week-cell-${idx}`}
                        className="px-6 py-2 break-words whitespace-normal text-primary text-sm font-medium"
                      >
                        {formatDaysOfWeek(policy.timeSpans)}
                      </TableCell>
                      <TableCell
                        key={`time-of-day-cell-${idx}`}
                        className="px-6 py-2 break-words whitespace-normal text-primary text-sm font-medium"
                      >
                        {formatTimeOfDay(policy.timeSpans)}
                      </TableCell>
                      <TableCell
                        key={`priority-cell-${idx}`}
                        className="px-6 py-2 whitespace-nowrap text-primary text-sm font-medium text-right"
                      >
                        {policy.priority}
                      </TableCell>
                      <TableCell
                        key={`action-cell-${idx}`}
                        className="px-6 py-2 whitespace-nowrap font-medium  flex flex-row gap-x-2 justify-center"
                      >
                        <IconButton
                          key={`edit-button-cell-${idx}`}
                          className="border-transparent text-secondary rounded-md w-10 h-10 hover:bg-secondary bg-primary-active p-0 m-0"
                          onClick={() => selectPolicy(policy)}
                        >
                          <EditIcon key={`edit-icon-cell-${idx}`} className="h-5 w-5" stroke="black" />
                        </IconButton>
                        <IconButton
                          key={`delete-button-cell-${idx}`}
                          className="border-transparent  rounded-md w-10 h-10 hover:bg-secondary  p-0 m-0"
                          onClick={() => onDelete(policy)}
                        >
                          <DeleteIcon key={`delete-icon-cell-${idx}`} className="h-5 w-5 " />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mb-2 flex flex-col items-end">
        <Pagination onChangePage={onChangePage} currentPage={currentPage.pageIndex} totalPage={totalPage} />
      </div>
    </div>
  );
};

interface GenNoRecordProps {
  colSpan?: number;
  message?: string;
}

export const GenNoRecord = ({ colSpan = 5, message = "No results found" }: GenNoRecordProps) => {
  return (
    <tr className="bg-default">
      <td className="px-6 py-4 whitespace-nowrap bg-white	 text-primary" colSpan={colSpan}>
        <div className="flex items-center justify-center">{message}</div>
      </td>
    </tr>
  );
};

export const EmptyData = () => (
  <div className="flex flex-col w-full gap-y-2 ">
    <div className="w-full bottom-2 pb-8 h-full">
      <Table className="w-full table-fixed">
        <TableHeader>
          <TableRow>
            {defaultCells.map((item, idx) => (
              <TableHead className={cn("px-6", item.className)} key={`policy-skeleton-header-${idx}`}>
                {item.title}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className="min-h-fit  max-h-full">
          <GenNoRecord message="Please select a city" colSpan={defaultCells.length} />
        </TableBody>
      </Table>
    </div>
  </div>
);
